import { LegacyRef, ReactNode, useEffect, useRef } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { posthog } from "@/lib/posthog";
import { ANALYTICS_EVENTS } from "@bash/shared";

export interface ITabItem {
  id: string;
  label: string;
  slug?: string;
  icon?: ReactNode;
  attn?: boolean;
  badge?: string | number;
  className?: string;
}

const Tabs = ({
  layout = 1,
  active,
  items,
  ariaLabel,
  clickContext,
  wrapperClass = "",
  itemPaddingVer = "py-2",
  paddingHor = "px-3",
  itemTextSize = "text-3.5",
  separator = true,
  onTabChange,
}: {
  layout?: 1 | 2 | 3;
  active: string;
  items: Array<ITabItem>;
  ariaLabel?: string;
  clickContext?: string;
  wrapperClass?: string;
  itemPaddingVer?: string;
  paddingHor?: string;
  itemTextSize?: string;
  separator?: boolean;
  onTabChange?: (id: any) => void;
}) => {
  const domRef = useRef<HTMLDivElement>(null);
  const activeIndex = items.findIndex((t) => t.id === active);

  useEffect(() => {
    const parent = domRef.current;
    if (parent && activeIndex >= 0) {
      const child = parent.children[activeIndex] as HTMLElement;
      if (child) {
        // Calculate the position to horizontally center the active tab
        const scrollTarget =
          child.offsetLeft - parent.offsetWidth / 2 + child.offsetWidth / 2;
        // Scroll ONLY the parent container horizontally
        parent.scrollTo({
          left: scrollTarget,
          behavior: "smooth",
        });
      }
    }
  }, [activeIndex]);

  if (layout === 1) {
    return (
      <div
        ref={domRef as LegacyRef<HTMLDivElement>}
        className="lib-tabs inline-flex gap-1 bg-bg-elevated border border-border-default rounded-100 p-1"
        role="tablist"
        aria-label={ariaLabel}
      >
        {items.map((tab) => {
          const TagName: any = tab.slug ? Link : "button";
          const isActive = active === tab.id;
          return (
            <TagName
              key={tab.id}
              {...(tab.slug
                ? { href: tab.slug, scroll: false, prefetch: true }
                : {})}
              role="tab"
              aria-selected={isActive}
              className={cn(
                "lib-tabs__tab font-sans text-[13px] font-medium text-fg-muted bg-transparent border-none py-[7px] px-4 rounded-100 cursor-pointer whitespace-nowrap transition-colors hover:text-fg-default",
                isActive
                  ? "lib-tabs__tab--on text-gold-ink bg-gold-molten-mid/8"
                  : ""
              )}
              onClick={() => {
                if (!isActive && clickContext) {
                  // These tabs swap the path in place — capture the switch explicitly.
                  posthog.capture(ANALYTICS_EVENTS.TAB_SWITCHED, {
                    from_tab: active,
                    to_tab: tab.id,
                    context: clickContext,
                  });
                }
              }}
            >
              {tab.label}
            </TagName>
          );
        })}
      </div>
    );
  }

  if (layout === 2) {
    return (
      <div
        ref={domRef as LegacyRef<HTMLDivElement>}
        className={cn(
          "bh-tabstrip max-w-full overflow-x-auto rounded-xl bg-bg-muted inline-flex p-1 sm:max-w-none hide-scrollbar",
          wrapperClass
        )}
        role="tablist"
      >
        {items.map((tab) => {
          const selected = active === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              className={cn(
                "bh-tab transition-bh-hover inline-flex h-8 items-center gap-1.75 rounded-[9px] px-3.5 text-3.5 font-medium whitespace-nowrap text-fg-muted hover:text-fg-default",
                selected && "bg-bg-elevated text-fg-default shadow-sm"
              )}
              aria-selected={selected}
              onClick={() => onTabChange?.(tab.id)}
            >
              {tab.attn && (
                <i
                  className="bh-tab__dot size-1.5 flex-none rounded-full bg-bh-brand"
                  aria-hidden="true"
                />
              )}
              {tab.label}
              {typeof tab.badge !== "undefined" && (
                <span
                  className={cn(
                    "bh-tab__n font-mono text-3",
                    tab.attn
                      ? "bh-tab__n--attn text-bh-brand font-semibold"
                      : selected
                        ? "text-hb-name"
                        : "text-fg-subtle"
                  )}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  if (layout === 3) {
    return (
      <nav
        ref={domRef as LegacyRef<HTMLDivElement>}
        className={`shrink-0 flex gap-1 py-2 ${paddingHor} bg-bg-base ${separator ? "border-b border-border-subtle" : ""} overflow-x-auto hide-scrollbar ${wrapperClass}`}
      >
        {items.map((tab) => {
          const TagName: any = tab.slug ? Link : "button";
          const isActive = active === tab.id;
          return (
            <TagName
              key={tab.id}
              {...(tab.slug ? { href: tab.slug, prefetch: true } : {})}
              role="tab"
              aria-selected={isActive}
              className={cn(
                "inline-flex items-center justify-center gap-1.5 px-3 min-w-15 font-medium text-fg-muted rounded-100 whitespace-nowrap shrink-0 transition-colors [&_svg]:w-3.5 [&_svg]:h-3.5 [&_svg]:shrink-0",
                itemPaddingVer,
                itemTextSize,
                isActive
                  ? "text-gold-ink bg-gold-molten-mid/8"
                  : "hover:bg-gold-molten-mid/8",
                tab.className
              )}
              onClick={() => onTabChange?.(tab.id)}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {typeof tab.badge !== "undefined" && (
                <span
                  className={cn(
                    "text-3 tabular-nums",
                    tab.id === active ? "text-fg-subtle" : "text-fg-faint"
                  )}
                >
                  {tab.badge}
                </span>
              )}
            </TagName>
          );
        })}
      </nav>
    );
  }

  return null;
};

export default Tabs;
